import { useCallback } from 'react';

import { Colors, typographyStyle } from '@revenuemarket/cds';
import { useFullSpinnerStore } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { mixpanelTrack } from '@revenuemarket/utils';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import { Dayjs } from 'dayjs';

import { DownloadDataType, DownloadFileByPostRequest } from '~apis/download';
import { convertRangeType, exportExcelFileByPost } from '~components/Export/export';
import { DatePickerRangeType } from '~components/ui/DatePicker/DateRangePickerWithPreset';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

type CommonExportExcelRequest = Pick<
  DownloadFileByPostRequest,
  'companyId' | 'endDate' | 'startDate'
> & {
  rangeType: DatePickerRangeType;
};

const dropdownMenuStyle = css`
    &&& .ant-dropdown-menu-item-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    &&& .ant-dropdown-menu-item-group-title {
      padding: 0 !important;
      ${typographyStyle['13_600']}
      color: ${Colors.gray500};
    }
    &&& .ant-dropdown-menu-item-group-list {
      margin: 0 !important;
    }
    &&& .ant-dropdown-menu-item {
      padding: 4px 8px;
      ${typographyStyle['14_400']}
      color: ${Colors.gray700};
    }
    &&& .ant-dropdown-menu-item-divider {
      margin: 12px 0 !important;
    }
  }
`;

const useExportExcel = ({ dataType }: { dataType: DownloadDataType }) => {
  const { showFullSpinner, hideFullSpinner } = useFullSpinnerStore();

  // mixpanel 타입 매핑
  const getMixpanelType = useCallback(() => {
    switch (dataType) {
      case 'BANK_TRANSACTION_DATA':
        return 'download transactions';
      case 'BANK_TRANSACTION_DATA_WEHAGO':
        return 'download transactions wehago';
      case 'BANK_TRANSACTION_LABELS':
        return 'download transactions labels';
      case 'CARD_APPROVAL_TRANSACTION_DATA':
        return 'download card approvals';
      case 'CARD_PURCHASE_TRANSACTION_DATA':
      case 'CARD_PURCHASE_TRANSACTION_DATA_WEHAGO':
        return 'download card purchases';
      case 'TAX_INVOICE_DATA':
        return 'download tax invoices';
      case 'CASH_RECEIPT_DATA':
        return 'download cash receipts';
      case 'CARD_BILLING_ITEM_LABELS':
        return 'download card billing items';
      case 'UNIFIED_CARD_TRANSACTION_LABELS':
        return 'download unified card transactions';
      case 'SALES_INVOICE_MAPPING':
      case 'PURCHASE_INVOICE_MAPPING':
        return 'download invoices mapping';
      case 'BANK_ACCOUNTS':
        return 'download bank accounts';
      case 'CASH_FLOW':
        return 'download cash flow';
      default:
        return 'download';
    }
  }, [dataType]);

  // 공통 export 함수
  const handleExport = useCallback(
    async (
      params: {
        companyId: string;
        startDate: Dayjs;
        endDate: Dayjs;
        rangeType: DatePickerRangeType;
        fileType: 'EXCEL' | 'ZIP';
        options?: Record<string, unknown>;
      },
      spinnerMessage?: string,
    ) => {
      showFullSpinner(spinnerMessage || '엑셀 파일 다운로드 중입니다');

      try {
        await exportExcelFileByPost({
          companyId: params.companyId,
          startDate: params.startDate,
          endDate: params.endDate,
          dataType,
          fileType: params.fileType,
          rangeType: convertRangeType(params.rangeType),
          ...params.options,
        });

        const mixpanelType = getMixpanelType();
        mixpanelTrack(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
          type: mixpanelType,
        });
      } catch {
        Toast.error('다운로드에 실패했습니다.');
      } finally {
        hideFullSpinner();
      }
    },
    [showFullSpinner, dataType, getMixpanelType, hideFullSpinner],
  );

  // 공통 그룹 생성 함수
  const createExportGroup = (
    label: string,
    children: ItemType[],
    style?: React.CSSProperties,
  ): ItemType => ({
    type: 'group' as const,
    label,
    style: style || {
      ...typographyStyle['14_400'],
      padding: 0,
    },
    children,
  });

  const dropdownItems = (() => {
    switch (dataType) {
      case 'BANK_TRANSACTION_DATA':
      case 'BANK_TRANSACTION_LABELS':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          bankAccountTransactionOptions,
          bankTransactionLabelsOptions,
          sort,
        }: CommonExportExcelRequest &
          Pick<
            DownloadFileByPostRequest,
            'bankAccountTransactionOptions' | 'bankTransactionLabelsOptions' | 'sort'
          >): ItemType[] => [
          createExportGroup('전체 내역', [
            {
              key: 'excel',
              label: '통합 파일 (xlsx)',
              onClick: async () => {
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'EXCEL',
                });
              },
            },
            {
              key: 'zip',
              label: '계좌별 파일 (zip)',
              onClick: async () => {
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'ZIP',
                });
              },
            },
          ]),
          { type: 'divider' },
          createExportGroup('필터가 적용된 내역', [
            {
              key: 'excel_filtered',
              label: '통합 파일 (xlsx)',
              onClick: async () => {
                const options =
                  dataType === 'BANK_TRANSACTION_DATA'
                    ? { bankAccountTransactionOptions, sort }
                    : { bankTransactionLabelsOptions, sort };
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'EXCEL',
                  options,
                });
              },
            },
            {
              key: 'zip_filtered',
              label: '계좌별 파일 (zip)',
              onClick: async () => {
                const options =
                  dataType === 'BANK_TRANSACTION_DATA'
                    ? { bankAccountTransactionOptions, sort }
                    : { bankTransactionLabelsOptions, sort };
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'ZIP',
                  options,
                });
              },
            },
          ]),
        ];
      case 'BANK_TRANSACTION_DATA_WEHAGO':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          bankAccountTransactionOptions,
          sort,
        }: CommonExportExcelRequest &
          Pick<
            DownloadFileByPostRequest,
            'bankAccountTransactionOptions' | 'sort'
          >): ItemType[] => [
          createExportGroup('전체 내역', [
            {
              key: 'excel',
              label: '통합 파일 (xlsx)',
              onClick: async () => {
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'EXCEL',
                });
              },
            },
            {
              key: 'zip',
              label: '계좌별 파일 (zip)',
              onClick: async () => {
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'ZIP',
                });
              },
            },
          ]),
          { type: 'divider' },
          createExportGroup('필터가 적용된 내역', [
            {
              key: 'excel_filtered',
              label: '통합 파일 (xlsx)',
              onClick: async () => {
                const options = { bankAccountTransactionOptions, sort };
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'EXCEL',
                  options,
                });
              },
            },
            {
              key: 'zip_filtered',
              label: '계좌별 파일 (zip)',
              onClick: async () => {
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'ZIP',
                  options: { bankAccountTransactionOptions, sort },
                });
              },
            },
          ]),
        ];
      case 'CARD_APPROVAL_TRANSACTION_DATA':
      case 'CARD_PURCHASE_TRANSACTION_DATA':
      case 'CARD_PURCHASE_TRANSACTION_DATA_WEHAGO':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          cardApprovalOptions,
          cardPurchaseOptions,
          purchaseSort,
          approvalSort,
        }: CommonExportExcelRequest & {
          purchaseSort?: string;
          approvalSort?: string;
        } & Pick<
            DownloadFileByPostRequest,
            'cardApprovalOptions' | 'cardPurchaseOptions'
          >): ItemType[] => [
          createExportGroup('전체 내역', [
            {
              key: 'full',
              label: '통합 파일 (xlsx)',
              onClick: async () => {
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'EXCEL',
                });
              },
            },
            {
              key: 'partial',
              label: '카드별 파일 (zip)',
              onClick: async () => {
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'ZIP',
                });
              },
            },
          ]),
          { type: 'divider' },
          createExportGroup('필터가 적용된 내역', [
            {
              key: 'full_filtered',
              label: '통합 파일 (xlsx)',
              onClick: async () => {
                const options =
                  dataType === 'CARD_APPROVAL_TRANSACTION_DATA'
                    ? { cardApprovalOptions, sort: approvalSort }
                    : { cardPurchaseOptions, sort: purchaseSort };
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'EXCEL',
                  options,
                });
              },
            },
            {
              key: 'partial_filtered',
              label: '카드별 파일 (zip)',
              onClick: async () => {
                const options =
                  dataType === 'CARD_APPROVAL_TRANSACTION_DATA'
                    ? { cardApprovalOptions, sort: approvalSort }
                    : { cardPurchaseOptions, sort: purchaseSort };
                await handleExport({
                  companyId,
                  startDate,
                  endDate,
                  rangeType,
                  fileType: 'ZIP',
                  options,
                });
              },
            },
          ]),
        ];
      case 'TAX_INVOICE_DATA':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          taxInvoiceOptions,
          sort,
        }: CommonExportExcelRequest &
          Pick<DownloadFileByPostRequest, 'taxInvoiceOptions' | 'sort'>): ItemType[] => [
          {
            key: 'full',
            label: '전체 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
              });
            },
          },
          {
            key: 'filter',
            label: '필터가 적용된 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
                options: { taxInvoiceOptions, sort },
              });
            },
          },
        ];
      case 'CASH_RECEIPT_DATA':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          cashReceiptOptions,
          sort,
        }: CommonExportExcelRequest &
          Pick<DownloadFileByPostRequest, 'cashReceiptOptions' | 'sort'>): ItemType[] => [
          {
            key: 'full',
            label: '전체 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
              });
            },
          },
          {
            key: 'filter',
            label: '필터가 적용된 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
                options: { cashReceiptOptions, sort },
              });
            },
          },
        ];
      case 'CARD_BILLING_ITEM_LABELS':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          cardLabelsOptions,
          cardBillingItemLabelsOptions,
          sort,
        }: CommonExportExcelRequest &
          Pick<
            DownloadFileByPostRequest,
            'cardLabelsOptions' | 'cardBillingItemLabelsOptions' | 'sort'
          >): ItemType[] => [
          {
            key: 'full',
            label: '전체 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
              });
            },
          },
          {
            key: 'filter',
            label: '필터가 적용된 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
                options: { cardLabelsOptions, cardBillingItemLabelsOptions, sort },
              });
            },
          },
        ];
      case 'UNIFIED_CARD_TRANSACTION_LABELS':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          unifiedCardTransactionLabelsOptions,
          sort,
        }: CommonExportExcelRequest &
          Pick<
            DownloadFileByPostRequest,
            'unifiedCardTransactionLabelsOptions' | 'sort'
          >): ItemType[] => [
          {
            key: 'full',
            label: '전체 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
              });
            },
          },
          {
            key: 'filter',
            label: '필터가 적용된 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
                options: { unifiedCardTransactionLabelsOptions, sort },
              });
            },
          },
        ];
      case 'PURCHASE_INVOICE_MAPPING':
      case 'SALES_INVOICE_MAPPING':
        return ({
          companyId,
          endDate,
          rangeType,
          startDate,
          salesInvoiceMappingOptions,
          purchaseInvoiceMappingOptions,
          sort,
        }: CommonExportExcelRequest &
          Pick<
            DownloadFileByPostRequest,
            'salesInvoiceMappingOptions' | 'purchaseInvoiceMappingOptions' | 'sort'
          >): ItemType[] => [
          {
            key: 'full',
            label: '전체 내역',
            onClick: async () => {
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
              });
            },
          },
          {
            key: 'filter',
            label: '필터가 적용된 내역',
            onClick: async () => {
              const options =
                dataType === 'SALES_INVOICE_MAPPING'
                  ? { salesInvoiceMappingOptions, sort }
                  : { purchaseInvoiceMappingOptions, sort };
              await handleExport({
                companyId,
                startDate,
                endDate,
                rangeType,
                fileType: 'EXCEL',
                options,
              });
            },
          },
        ];
      default:
        return null;
    }
  })();

  return { dropdownItems, dropdownMenuStyle, handleExport };
};

export default useExportExcel;
